/** Resize and compress images before inspection/KYC uploads (mobile cameras often exceed 5MB). */

const DEFAULT_MAX_BYTES = 4.5 * 1024 * 1024;
const DEFAULT_MAX_EDGE = 1920;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Image compression failed"))),
      "image/jpeg",
      quality,
    );
  });
}

/**
 * @param {File|Blob} file
 * @param {{ maxBytes?: number, maxEdge?: number }} opts
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file, opts = {}) {
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;

  if (!file || !(file instanceof Blob)) {
    throw new Error("No image provided");
  }

  // Skip tiny files — already fine.
  if (file.size <= maxBytes && file.type === "image/jpeg") {
    const name = file.name?.replace(/\.[^.]+$/, ".jpg") || "photo.jpg";
    return file instanceof File ? file : new File([file], name, { type: "image/jpeg" });
  }

  const img = await loadImageFromFile(file);
  let { width, height } = img;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  width = Math.max(1, Math.round(width * scale));
  height = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, 0, 0, width, height);

  let quality = 0.88;
  let blob = await canvasToBlob(canvas, quality);
  while (blob.size > maxBytes && quality > 0.45) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, quality);
  }

  if (blob.size > maxBytes) {
    throw new Error("Photo is still too large after compression — move closer or retake with lower resolution.");
  }

  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "") || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

export function formatUploadError(detail) {
  if (!detail) return "Upload failed";
  if (detail === "file_too_large") return "Photo too large (max 10MB). Retake or move closer — the app will compress on retry.";
  if (typeof detail === "string" && detail.startsWith("unsupported_file_type")) {
    return "Use JPG, PNG, or WEBP photos.";
  }
  if (typeof detail === "string" && detail.startsWith("session_not_active")) {
    return "This scan session expired. Refresh the QR code and try again.";
  }
  if (detail === "session_not_found") return "This scan link expired or is invalid.";
  if (detail === "gallery_upload_not_allowed") {
    return "Trip inspections require live camera photos — gallery uploads are not accepted.";
  }
  if (detail === "duplicate_photo_in_inspection") {
    return "Each angle must be a distinct photo — reuse detected.";
  }
  return detail;
}
